/**
 * Refund Modal (Partial Refund / Devolução Parcial)
 * Search a finalized sale by number, select items and quantities to return.
 * Requires manager password.
 */
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Lock, Package, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

interface SaleItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  unit: string;
  subtotal: number;
  returned: boolean;
  returned_quantity: number;
}

interface Sale {
  id: string;
  numero: number;
  status: string;
  total: number;
  data_venda: string;
  items: SaleItem[];
}

interface RefundSelection {
  item_id: string;
  quantity: number;
  max_quantity: number;
  product_name: string;
  unit_price: number;
}

interface RefundModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function RefundModal({ isOpen, onClose, onSuccess }: RefundModalProps) {
  const [step, setStep] = useState<'search' | 'select' | 'confirm'>('search');
  const [searchNumber, setSearchNumber] = useState('');
  const [sale, setSale] = useState<Sale | null>(null);
  const [selections, setSelections] = useState<RefundSelection[]>([]);
  const [reason, setReason] = useState('');
  const [managerPassword, setManagerPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const resetForm = useCallback(() => {
    setStep('search');
    setSearchNumber('');
    setSale(null);
    setSelections([]);
    setReason('');
    setManagerPassword('');
    setLoading(false);
    setAuthError('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSearch = useCallback(async () => {
    if (!searchNumber.trim()) {
      toast.error('Informe o número da venda');
      return;
    }

    setLoading(true);
    try {
      // Search sale by number - we'll use the sale ID endpoint
      // First we need to find the sale by number
      const response = await apiFetch(`/api/sales/${searchNumber.trim()}`);

      if (!response.ok) {
        toast.error('Venda não encontrada');
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.status !== 'finalizada') {
        toast.error('Apenas vendas finalizadas podem ter devolução parcial');
        setLoading(false);
        return;
      }

      setSale(data);
      setStep('select');
    } catch {
      toast.error('Erro ao buscar venda');
    }
    setLoading(false);
  }, [searchNumber]);

  const toggleItem = useCallback((item: SaleItem) => {
    setSelections((prev) => {
      const existing = prev.find((s) => s.item_id === item.id);
      if (existing) {
        return prev.filter((s) => s.item_id !== item.id);
      }
      const maxQty = Number(item.quantity) - Number(item.returned_quantity || 0);
      return [
        ...prev,
        {
          item_id: item.id,
          quantity: maxQty,
          max_quantity: maxQty,
          product_name: item.product_name,
          unit_price: Number(item.unit_price),
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((itemId: string, delta: number) => {
    setSelections((prev) =>
      prev.map((s) => {
        if (s.item_id === itemId) {
          const newQty = Math.max(0.001, Math.min(s.max_quantity, s.quantity + delta));
          return { ...s, quantity: newQty };
        }
        return s;
      }),
    );
  }, []);

  const totalRefund = selections.reduce(
    (sum, s) => sum + Math.round(s.quantity * s.unit_price * 100) / 100,
    0,
  );

  const handleConfirm = useCallback(async () => {
    if (!sale) return;

    if (selections.length === 0) {
      toast.error('Selecione ao menos um item para devolução');
      return;
    }

    if (!reason.trim()) {
      toast.error('Informe o motivo da devolução');
      return;
    }

    if (!managerPassword) {
      toast.error('Senha do gerente é obrigatória');
      return;
    }

    setLoading(true);
    setAuthError('');

    try {
      const response = await apiFetch(`/api/sales/${sale.id}/partial-refund`, {
        method: 'POST',
        body: JSON.stringify({
          reason,
          manager_password: managerPassword,
          items: selections.map((s) => ({
            item_id: s.item_id,
            quantity: s.quantity,
          })),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error?.includes('Senha do gerente')) {
          setAuthError('Senha do gerente inválida');
          setLoading(false);
          return;
        }
        toast.error(data.error || 'Erro ao processar devolução');
        setLoading(false);
        return;
      }

      toast.success(
        `Devolução parcial processada. Valor: R$${data.refund_total.toFixed(2)}`,
      );
      onSuccess();
      handleClose();
    } catch {
      toast.error('Erro de conexão');
      setLoading(false);
    }
  }, [sale, selections, reason, managerPassword, onSuccess, handleClose]);

  const formatCurrency = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className="relative z-10 w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-xl"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
          >
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 rounded-sm p-1 text-muted-foreground hover:text-foreground"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>

            <h2 className="text-lg font-semibold">Devolução Parcial</h2>

            {/* Step: Search */}
            {step === 'search' && (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Busque a venda pelo ID para realizar a devolução parcial.
                </p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchNumber}
                      onChange={(e) => setSearchNumber(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="ID da venda"
                      className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                      autoFocus
                    />
                  </div>
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                  >
                    {loading ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
              </div>
            )}

            {/* Step: Select Items */}
            {step === 'select' && sale && (
              <div className="mt-4 space-y-4">
                <div className="rounded-lg border border-border/50 bg-muted/30 p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Venda #{sale.numero}</span>
                    <span className="font-medium">{formatCurrency(Number(sale.total))}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(sale.data_venda).toLocaleString('pt-BR')}
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">
                  Selecione os itens e quantidades para devolução:
                </p>

                <div className="max-h-48 space-y-2 overflow-y-auto">
                  {sale.items.map((item) => {
                    const maxReturnable = Number(item.quantity) - Number(item.returned_quantity || 0);
                    const isSelected = selections.some((s) => s.item_id === item.id);
                    const selection = selections.find((s) => s.item_id === item.id);

                    if (maxReturnable <= 0) {
                      return (
                        <div
                          key={item.id}
                          className="flex items-center gap-3 rounded-lg border border-border/30 bg-muted/20 p-3 opacity-50"
                        >
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span className="flex-1 text-sm line-through">{item.product_name}</span>
                          <span className="text-xs text-muted-foreground">Já devolvido</span>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 rounded-lg border p-3 transition-colors cursor-pointer ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border/50 hover:border-primary/30'
                        }`}
                        onClick={() => toggleItem(item)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleItem(item)}
                          className="h-4 w-4 rounded border-border accent-primary"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium">{item.product_name}</span>
                          <div className="text-xs text-muted-foreground">
                            {formatCurrency(Number(item.unit_price))} × {Number(item.quantity)} {item.unit}
                          </div>
                        </div>
                        {isSelected && selection && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => updateQuantity(item.id, -1)}
                              className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="min-w-[2rem] text-center text-sm font-medium">
                              {selection.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, 1)}
                              className="rounded p-0.5 text-muted-foreground hover:bg-muted"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selections.length > 0 && (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <div className="flex justify-between text-sm font-medium">
                      <span>Total da devolução:</span>
                      <span className="text-primary">{formatCurrency(totalRefund)}</span>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setStep('confirm')}
                  disabled={selections.length === 0}
                  className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Continuar ({selections.length} {selections.length === 1 ? 'item' : 'itens'})
                </button>
              </div>
            )}

            {/* Step: Confirm with reason and manager password */}
            {step === 'confirm' && (
              <div className="mt-4 space-y-4">
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total da devolução:</span>
                    <span className="text-primary">{formatCurrency(totalRefund)}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {selections.length} {selections.length === 1 ? 'item' : 'itens'} selecionado(s)
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Motivo da devolução *
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Ex: Produto com defeito, cliente desistiu..."
                    rows={2}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <Lock className="h-3.5 w-3.5" />
                    Senha do Gerente *
                  </label>
                  <input
                    type="password"
                    value={managerPassword}
                    onChange={(e) => {
                      setManagerPassword(e.target.value);
                      setAuthError('');
                    }}
                    placeholder="Digite a senha do gerente"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  {authError && (
                    <p className="mt-1 text-xs text-destructive">{authError}</p>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setStep('select')}
                    className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                  >
                    Voltar
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={loading || !reason.trim() || !managerPassword}
                    className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? 'Processando...' : 'Confirmar Devolução'}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
